import * as z from "zod";

export const listStructureAreasResponseSchema = z.object({
  areas: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      sortOrder: z.number(),
      itemCount: z.number(),
      chipIds: z.array(z.string()).default([]),
    }),
  ),
});

export type ListStructureAreasResponse = z.infer<
  typeof listStructureAreasResponseSchema
>;

export const listStructureItemsInputSchema = z.object({
  structureAreaId: z.string(),
});

export type ListStructureItemsInput = z.infer<
  typeof listStructureItemsInputSchema
>;

export const listStructureItemsResponseSchema = z.object({
  items: z.array(
    z.object({
      id: z.string(),
      structureAreaId: z.string(),
      name: z.string(),
      atomicItemId: z.string(),
      sortOrder: z.number(),
      chipIds: z.array(z.string()).default([]),
    }),
  ),
});

export type ListStructureItemsResponse = z.infer<
  typeof listStructureItemsResponseSchema
>;

export const createStructureItemInputSchema = z.object({
  organizationId: z.string(),
  structureAreaId: z.string(),
  name: z.string().trim().min(1).max(120),
});

export type CreateStructureItemInput = z.infer<
  typeof createStructureItemInputSchema
>;

export const createStructureItemResponseSchema = z.object({
  item: z.object({
    id: z.string(),
    structureAreaId: z.string(),
    name: z.string(),
    atomicItemId: z.string(),
    sortOrder: z.number(),
    chipIds: z.array(z.string()).default([]),
  }),
});

export type CreateStructureItemResponse = z.infer<
  typeof createStructureItemResponseSchema
>;

export const createStructureAreaInputSchema = z.object({
  organizationId: z.string(),
  name: z.string().trim().min(1).max(120),
});

export type CreateStructureAreaInput = z.infer<
  typeof createStructureAreaInputSchema
>;

export const createStructureAreaResponseSchema = z.object({
  area: z.object({
    id: z.string(),
    name: z.string(),
    sortOrder: z.number(),
    itemCount: z.number(),
    chipIds: z.array(z.string()).default([]),
  }),
});

export type CreateStructureAreaResponse = z.infer<
  typeof createStructureAreaResponseSchema
>;

export const updateStructureAreaInputSchema = z.object({
  organizationId: z.string(),
  name: z.string().trim().min(1).max(120).optional(),
  sortOrder: z.number().int().optional(),
});

export type UpdateStructureAreaInput = z.infer<
  typeof updateStructureAreaInputSchema
>;

export const updateStructureAreaResponseSchema = z.object({
  area: z.object({
    id: z.string(),
    name: z.string(),
    sortOrder: z.number(),
    itemCount: z.number(),
    chipIds: z.array(z.string()).default([]),
  }),
});

export const structureChipSchema = z.object({
  id: z.string(),
  label: z.string(),
  category: z.string().nullish(),
  createdAt: z.coerce.date().optional(),
});

export const listStructureChipsInputSchema = z.object({
  organizationId: z.string(),
});

export const listStructureChipsResponseSchema = z.object({
  chips: z.array(structureChipSchema),
});

export const createStructureChipInputSchema = z.object({
  organizationId: z.string(),
  label: z.string().trim().min(1).max(80),
  category: z.string().trim().min(1).max(40).nullish(),
});

export const createStructureChipResponseSchema = z.object({
  chip: structureChipSchema,
});

export const updateStructureChipInputSchema = z.object({
  organizationId: z.string(),
  label: z.string().trim().min(1).max(80),
  category: z.string().trim().min(1).max(40).nullish(),
});

export const updateStructureChipResponseSchema = z.object({
  chip: structureChipSchema,
});

export const deleteStructureChipInputSchema = z.object({
  organizationId: z.string(),
});

export const deleteStructureChipResponseSchema = z.object({
  ok: z.literal(true),
  chipId: z.string(),
});

export const setStructureAreaChipsInputSchema = z.object({
  organizationId: z.string(),
  chipIds: z.array(z.string()),
});

export const setStructureAreaChipsResponseSchema = z.object({
  ok: z.literal(true),
  areaId: z.string(),
  chipIds: z.array(z.string()),
});

export const setStructureItemChipsInputSchema = z.object({
  organizationId: z.string(),
  chipIds: z.array(z.string()),
});

export const setStructureItemChipsResponseSchema = z.object({
  ok: z.literal(true),
  itemId: z.string(),
  chipIds: z.array(z.string()),
});

export const bulkApplyStructureItemChipsInputSchema = z.object({
  organizationId: z.string(),
  itemIds: z.array(z.string()).min(1),
  chipIds: z.array(z.string()),
});

export const bulkApplyStructureItemChipsResponseSchema = z.object({
  ok: z.literal(true),
  itemIds: z.array(z.string()),
  chipIds: z.array(z.string()),
});

export type UpdateStructureAreaResponse = z.infer<
  typeof updateStructureAreaResponseSchema
>;

export const deleteStructureAreaInputSchema = z.object({
  organizationId: z.string(),
});

export const deleteStructureAreaResponseSchema = z.object({
  ok: z.literal(true),
  areaId: z.string(),
});

export type DeleteStructureAreaInput = z.infer<
  typeof deleteStructureAreaInputSchema
>;

export type DeleteStructureAreaResponse = z.infer<
  typeof deleteStructureAreaResponseSchema
>;

export const assignStructureItemInputSchema = z.object({
  organizationId: z.string(),
  structureAreaId: z.string(),
  atomicItemId: z.string(),
});

export type AssignStructureItemInput = z.infer<
  typeof assignStructureItemInputSchema
>;

export const assignStructureItemResponseSchema = z.object({
  item: z.object({
    id: z.string(),
    structureAreaId: z.string(),
    name: z.string(),
    atomicItemId: z.string(),
    sortOrder: z.number(),
    chipIds: z.array(z.string()).default([]),
  }),
});

export type AssignStructureItemResponse = z.infer<
  typeof assignStructureItemResponseSchema
>;

export const deleteStructureItemInputSchema = z.object({
  organizationId: z.string(),
});

export const deleteStructureItemResponseSchema = z.object({
  ok: z.literal(true),
  itemId: z.string(),
});

export type DeleteStructureItemInput = z.infer<
  typeof deleteStructureItemInputSchema
>;

export type DeleteStructureItemResponse = z.infer<
  typeof deleteStructureItemResponseSchema
>;
